import { Typography } from "@mui/material";
import CheckIcon from "./icons/CheckIcon";
import EditIcon from "./icons/EditIcon";
import { useState, createContext, useContext } from "react";
import PlanDetailsModal from "./PlanDetailsModal";
import ZipCodeDialog from "./ZipCodeDialog";
import { useLocation, useNavigate } from "react-router-dom";
import { getCurrentPromoCode } from "../../utils/promoCodeUtils";
import { Box } from "@mui/material";
import { usePlanContext } from "./context/planContext";
import { handleSignUp } from "./utils";
import { ComparisonData } from "../../types";
//importing all style components from components file
import {
  PricingPlansWrapper,
  PricingFiltersContainer,
  PricingFiltersWrapper,
  FilterButton,
  PricingCardTitle,
  BestValueBadge,
  BestValueText,
  PriceDisplay,
  PriceValue,
  PriceUnit,
  SignUpButton,
  FeatureItem,
  FeatureText,
  ViewDetailsLink,
  SavingsContainer,
  SavingsBox,
  SavingsLabel,
  SavingsBottom,
  SavingsValue,
  SeeComparisonLink,
  ModalPriceCent,
  ZipBannerWrapper,
  ZipBannerContent,
  ZipBannerImageBox,
  ZipBannerImage,
  ZipBannerTextCol,
  ZipBannerTextGroup,
  ZipBannerSubText,
  ZipBannerHeadline,
  ZipBannerButton,
  ZipBannerButtonText,
  PricingFiltersTitle,
  DiscoverPricingCard,
  DiscoverPricingCardsGrid,
} from "./styles/components";
import ZipCodeBanner from "./ZipCodebanner";
export const ComparisonContext = createContext<
  | {
      comparisonData: ComparisonData[] | null;
      setComparisonData: (data: ComparisonData[] | null) => void;
    }
  | undefined
>(undefined);
export const ComparisonProvider = ComparisonContext.Provider;
export const useComparison = () => {
  const context = useContext(ComparisonContext);
  if (!context)
    throw new Error("useComparison must be used within ComparisonProvider");
  return context;
};

const DiscoverAllPlanns = () => {
  const location = useLocation();
  const routeBasedPromoCode = getCurrentPromoCode(location.pathname);
  const [modalOpen, setModalOpen] = useState(false);
  const [cardTransforms, setCardTransforms] = useState<{
    [key: number]: { x: number; y: number };
  }>({});
  const [zipDialogOpen, setZipDialogOpen] = useState(false);
  const {
    plansLoading,
    plans,
    setZipCode,
    zipCode,
    setSelectedPlan,
    selectedPlan,
    utility,
    locationDenied,
    bestValuePlan,
  } = usePlanContext();
  const navigate = useNavigate(); // Move useNavigate to top level to avoid conditional hook call
  const handleViewDetails = (plan: any) => {
    setSelectedPlan(plan);
    setModalOpen(true);
  };
  const handleCloseModal = () => {
    setModalOpen(false);
  };

  const handleScrollToComparison = async (plan: any) => {
    const comparisonSection = document.getElementById("comparison-section");
    if (comparisonSection) {
      comparisonSection.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  };
  const handleMouseMove = (
    e: React.MouseEvent<HTMLDivElement>,
    index: number,
  ) => {
    const card = e.currentTarget;
    const rect = card.getBoundingClientRect();
    const x = (e.clientX - rect.left - rect.width / 2) / rect.width;
    const y = (e.clientY - rect.top - rect.height / 2) / rect.height;
    setCardTransforms((prev) => ({
      ...prev,
      [index]: { x: x * 20, y: y * 20 },
    }));
  };
  const handleMouseLeave = (index: number) => {
    setCardTransforms((prev) => {
      const newTransforms = { ...prev };
      newTransforms[index] = { x: 0, y: 0 };
      return newTransforms;
    });
  };
  const handleZipCodeClick = () => {
    setZipDialogOpen(true);
  };
  const handleZipCodeClose = () => {
    setZipDialogOpen(false);
  };
  const handleZipCodeSave = (newZipCode: string) => {
    setZipCode(newZipCode);
    setZipDialogOpen(false);
  };

  // Clean up page_path from localStorage when component unmounts
  // useEffect(() => {
  //   return () => {
  //     localStorage.removeItem("page_path");
  //   };
  // }, []);

  // Show ZIP code banner if location access denied and no plans available for current ZIP
  if (locationDenied && !plansLoading && plans.length === 0) {
    return <ZipCodeBanner />;
  }

  // Hide component if no ZIP code is set
  if (!zipCode) {
    return null;
  }
  // Handle empty plans array
  if (!plansLoading && plans.length === 0) {
    return (
      <ZipBannerWrapper>
        <ZipBannerContent>
          <ZipBannerImageBox>
            <ZipBannerImage
              src="/images/constenewImg/locationBanner.png"
              alt="Location Banner"
            />
          </ZipBannerImageBox>
          <ZipBannerTextCol>
            <ZipBannerTextGroup>
              <ZipBannerSubText>
                No plans available for {zipCode} yet
              </ZipBannerSubText>
              <ZipBannerHeadline>
                Change your ZIP to see the best local plans.
              </ZipBannerHeadline>
            </ZipBannerTextGroup>
            <ZipBannerButton variant="contained" onClick={handleZipCodeClick}>
              <Box
                component="img"
                src="/images/constenewImg/tdesign_location-filled-white.svg"
                alt="Location"
                sx={{ width: "20px", height: "20px" }}
              />
              <ZipBannerButtonText>Change your ZIP</ZipBannerButtonText>
            </ZipBannerButton>
            <ZipCodeDialog
              open={zipDialogOpen}
              zipCode={zipCode}
              onClose={handleZipCodeClose}
              onSave={handleZipCodeSave}
            />
          </ZipBannerTextCol>
        </ZipBannerContent>
      </ZipBannerWrapper>
    );
  }
  
  // Sort plans by price (lowest to highest)
  const sortedPlans = [...plans].sort(
    (a, b) => parseFloat(a.price) - parseFloat(b.price),
  );
  // Find the best value plan in sorted array
  // Find the lowest price 'A/C Protect Plus' plan
  const acProtectPlusPlans = sortedPlans.filter(
    (p) => p.plan_name && p.plan_name.toLowerCase().includes("protect plus"),
  );
  const ACprotectPlan =
    acProtectPlusPlans.length > 0 ? acProtectPlusPlans[0] : null;
  let displayPlans = [];
  // Best value plan (first/top position)
    if (bestValuePlan)
    displayPlans.push({
      ...bestValuePlan,
      highlight: true,
      badge: "Best Value",
        isComparsionlink: //If BestValuePlan have Contract term 12 months AND Plan name must include "12 month usage bill credit"
        Number(bestValuePlan.contract_term) === 12 && 
        bestValuePlan?.plan_name.toLowerCase().includes("12 month usage bill credit"),
    });

  // Second plan (if exists and not best value or ACprotect)
  if (sortedPlans.length > 0) {
    const secondPlan = sortedPlans.find(
      (p) =>
        p.plan_id !== bestValuePlan?.plan_id &&
        p.plan_id !== ACprotectPlan?.plan_id,
    );
    if (secondPlan)
      displayPlans.push({
        ...secondPlan,
        highlight: false,
        badge: undefined,
        isComparsionlink: //Check if the Secondplan is Contract term 12 months AND Plan name must include "12 month usage bill credit"
       Number(secondPlan.contract_term) === 12 && 
          secondPlan?.plan_name.toLowerCase().includes("12 month usage bill credit"),
      });
  }

  // A/C Protect Plus plan
  if (ACprotectPlan)
    displayPlans.push({
      ...ACprotectPlan,
      highlight: false,
      badge: undefined,
      isComparsionlink: false,
    });

  // Fill with all other plans (no duplicates)
  for (let plan of sortedPlans) {
    if (!displayPlans.some((p) => p.plan_id === plan.plan_id)) {
      displayPlans.push({
        ...plan,
        highlight: false,
        badge: undefined,
        isComparsionlink: false,
      });
    }
  }

  //  all AllPlans is true
  const plansToDisplay = displayPlans;
  return (
    <Box id="discover-all-plans">
      <PricingPlansWrapper id="pricing-section">
        <>
          <PricingFiltersContainer>
            <PricingFiltersTitle>Discover all plans</PricingFiltersTitle>
            <PricingFiltersWrapper>
              <FilterButton variant="text" onClick={handleZipCodeClick}>
                <Box
                  component="img"
                  src="/images/constenewImg/tdesign_location-filled.svg"
                  alt="Location"
                  sx={{ width: "20px", height: "20px" }}
                />
                Your ZIP: {zipCode}
                <EditIcon size={16} />
              </FilterButton>
            </PricingFiltersWrapper>
          </PricingFiltersContainer>

          <DiscoverPricingCardsGrid>
            {plansToDisplay.map((plan, index) => {
              const transform = cardTransforms[index] ?? { x: 0, y: 0 };
              const hasTransform =
                Math.abs(transform.x) + Math.abs(transform.y) > 0;
              return (
                <DiscoverPricingCard
                  key={index}
                  highlight={plan.highlight}
                  hasTransform={hasTransform}
                  transformX={transform.x}
                  transformY={transform.y}
                  onMouseMove={(e) => handleMouseMove(e, index)}
                  onMouseLeave={() => handleMouseLeave(index)}
                >
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "flex-start",
                      alignItems: "stretch",
                      flexDirection: { xs: "column", sm: "row" },
                    }}
                  >
                    <Box
                      sx={{
                        p: { xs: 2, md: 3 },
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "space-between",
                        // minHeight: "280px",
                        height: "auto",
                        gap: { xs: 2, md: 1 },
                        // maxWidth: {xs:"100%", md:"280px"},
                        width: { xs: "100%", md: "370px !important" },
                        borderRadius: "18px 0px 0px 18px",
                        borderRight: { xs: "none", md: "1px solid #DDE4EA" },
                      }}
                    >
                      <Box>
                        <PricingCardTitle>
                          <Typography
                            component="span"
                            sx={{
                              color: "#2372b9",
                              textAlign: "left",
                              py: 1,
                              fontSize: {
                                xs: "14px !important",
                                md: "16px !important",
                              },
                              fontWeight: "bold",
                              p: 0,
                            }}
                          >
                            {" "}
                            {plan.plan_name}
                          </Typography>
                          {plan.badge && (
                            <BestValueBadge>
                              <BestValueText>Best Value</BestValueText>
                            </BestValueBadge>
                          )}
                        </PricingCardTitle>
                      </Box>
                      <Box sx={{ textAlign: "center" , my:{xs:1, md:1.5}}}>
                        <PriceDisplay
                          sx={{
                            textAlign: "left",
                            display: "flex",
                            justifyContent: { xs: "left", md: "center" },
                            alignItems: "center",
                          }}
                        >
                          <PriceValue>{plan.price1000}</PriceValue>
                          <ModalPriceCent> ¢ </ModalPriceCent>
                          <PriceUnit>per kWh</PriceUnit>
                        </PriceDisplay>
                      </Box>
                      <Box>
                        <SignUpButton
                          variant="contained"
                          onClick={() => {
                            setSelectedPlan(plan);
                            localStorage.setItem(
                              "selectProduct",
                              JSON.stringify(plan),
                            );
                            localStorage.setItem(
                              "utility",
                              JSON.stringify(utility),
                            );
                            localStorage.setItem(
                              "promoCode",
                              routeBasedPromoCode,
                            );
                            localStorage.setItem(
                              "page_path",
                              window.location.pathname,
                            );
                            handleSignUp(
                              plan,
                              zipCode,
                              utility,
                              navigate,
                              location,
                            );
                          }}
                        >
                          Select Plan
                        </SignUpButton>
                      </Box>
                    </Box>
                    <Box
                      sx={{
                        backgroundColor: "#ffffff00",
                        // minHeight: "280px",
                        width: "100%",
                        borderRadius: "0px 18px 18px 0px",
                      }}
                    >
                      <Box
                        sx={{
                          height: "50px",
                          // backgroundColor: "#ebf1f6bb",
                          display: { sm: "block", xs: "none" },
                        }}
                      >
                        <Typography
                          sx={{
                            color: "#2372b9",
                            textAlign: "left",
                            py: 1,
                            fontSize: {
                              xs: "1.25rem", // h6
                              md: "1.5rem", // h5
                            },
                            fontWeight: "bold",
                            px: 3,
                            pt: 3,
                          }}
                        >
                          {" "}
                          {plan.plan_name}
                        </Typography>
                      </Box>
                      <Box
                        sx={{
                          p: { xs: 2, sm: 3, md: 4, lg: 5 },
                          pb: { xs: 3, md: 2, lg: 2 },
                          display: "flex",
                          flexDirection: "column",
                          justifyContent: "space-between",
                        }}
                      >
                        <Box
                          sx={{
                            gap: 1,
                            display: "flex",
                            flexDirection: "column",
                          }}
                        >
                          {plan.features
                            .slice(0, 2)
                            .map((feature: string, idx: number) => (
                              <FeatureItem key={idx}>
                                <CheckIcon size={16} />
                                <FeatureText
                                  sx={{ fontSize: { xs: "14px", md: "16px" } }}
                                >
                                  {feature}
                                </FeatureText>
                              </FeatureItem>
                            ))}
                        </Box>
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: plan.isComparsionlink ? "space-between" : "flex-end",
                            marginTop: "12px",
                            marginLeft: "24px",
                          }}
                        >
                          {
                             plan.isComparsionlink && (
                                 <Box>
                            <SeeComparisonLink
                              onClick={() => {
                                handleScrollToComparison(plan);
                              }}
                            >
                              See comparison
                            </SeeComparisonLink>
                          </Box>)
                          }
                       
                          <ViewDetailsLink
                            onClick={() => handleViewDetails(plan)}
                          >
                            View details
                          </ViewDetailsLink>
                        </Box>
                        {/* <Box sx={{ mt: 2 }}>
                          <SavingsContainer>
                            {plan.highlight ? (
                              <SavingsBox highlight={plan.highlight}>
                                <SavingsLabel>
                                  Estimated Annual Savings
                                </SavingsLabel>
                                <SavingsBottom>
                                  {plan?.savings ? (
                                    <SavingsValue>{plan.savings}</SavingsValue>
                                  ) : null}
                                  <SeeComparisonLink
                                    onClick={() => {
                                      handleScrollToComparison(plan);
                                    }}
                                  >
                                    See comparison
                                  </SeeComparisonLink>
                                </SavingsBottom>
                              </SavingsBox>
                            ) : null}
                          </SavingsContainer>
                        </Box> */}
                      </Box>
                    </Box>
                  </Box>

                  {/* <PricingCardInner>
                  <PricingCardHeader>
                    <PricingCardTitle>
                      <PlanName>{plan.plan_name}</PlanName>
                      {plan.badge && (
                        <BestValueBadge>
                          <BestValueText>Best Value</BestValueText>
                        </BestValueBadge>
                      )}
                    </PricingCardTitle>
                  </PricingCardHeader>
                  <PricingCardBody>
                    <PriceDisplay>
                      <PriceValue>{plan.price1000}</PriceValue>
                      <ModalPriceCent> ¢ </ModalPriceCent>
                      <PriceUnit>per kWh</PriceUnit>
                    </PriceDisplay>
                    <SignUpButton
                      variant="contained"
                      onClick={() => {
                        setSelectedPlan(plan);
                        localStorage.setItem(
                          "selectProduct",
                          JSON.stringify(plan),
                        );
                        localStorage.setItem(
                          "utility",
                          JSON.stringify(utility),
                        );
                        localStorage.setItem("promoCode", routeBasedPromoCode);
                        localStorage.setItem(
                          "page_path",
                          window.location.pathname,
                        );
                        handleSignUp(
                          plan,
                          zipCode,
                          utility,
                          navigate,
                          location,
                        );
                      }}
                    >
                      Select Plan
                    </SignUpButton>
                    <FeaturesContainer>
                      {plan.features.map((feature: string, idx: number) => (
                        <FeatureItem key={idx}>
                          <CheckIcon size={16} />
                          <FeatureText>{feature}</FeatureText>
                        </FeatureItem>
                      ))}
                      <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                        <ViewDetailsLink
                          onClick={() => handleViewDetails(plan)}
                        >
                          View details
                        </ViewDetailsLink>
                      </Box>
                    </FeaturesContainer>
                    <SavingsContainer>
                      {plan.highlight ? (
                        <SavingsBox highlight={plan.highlight}>
                          <SavingsLabel>Estimated Annual Savings</SavingsLabel>
                          <SavingsBottom>
                            {plan?.savings ? (
                              <SavingsValue>{plan.savings}</SavingsValue>
                            ) : null}
                            <SeeComparisonLink
                              onClick={() => {
                                handleScrollToComparison(plan);
                              }}
                            >
                              See comparison
                            </SeeComparisonLink>
                          </SavingsBottom>
                        </SavingsBox>
                      ) : null}
                    </SavingsContainer>
                  </PricingCardBody>
                </PricingCardInner> */}
                </DiscoverPricingCard>
              );
            })}
          </DiscoverPricingCardsGrid>

          <PlanDetailsModal
            open={modalOpen}
            onClose={handleCloseModal}
            plan={selectedPlan}
          />
          <ZipCodeDialog
            open={zipDialogOpen}
            zipCode={zipCode}
            onClose={handleZipCodeClose}
            onSave={handleZipCodeSave}
          />
        </>
      </PricingPlansWrapper>
    </Box>
  );
};

export default DiscoverAllPlanns;
